import React, { useState } from 'react';

import { useSelector } from 'react-redux';
import ModalState from '@redux/modal/modal-state.interface';

import styles from './modal.styles';
import Portal from './portal';

import { hideModal } from '@redux/modal/modal-dispatcher';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

interface ModalVariant {
    containerClass?: string;
    contentWrapperClass?: string;
    closeElement?: React.ReactNode;
}

interface ModalProps extends ModalVariant {
    id: string;
    title?: string;
    width?: string;

    onClose?: () => void;

    variant?: 'default' | 'lightbox';
    titleBarClass?: string;
    stopPropagation?: boolean;
}

const variants: { [label: string]: ModalVariant } = {
    default: {
        containerClass: 'bg-white mx-auto rounded-xl shadow-lg z-50 overflow-y-auto',
        contentWrapperClass: 'relative text-left py-4 px-6',
        closeElement: (
            <div className="modal-close z-50">
                <FontAwesomeIcon icon={faTimes} />
            </div>
        ),
    },
    lightbox: {
        containerClass:
            'relative overflow-visible bg-boon-beige box-shadow rounded custom-bg relative opacity-100 md:w-full w-11/12',
        contentWrapperClass: '',
        closeElement: (
            <React.Fragment>
                <div className="invisible modal-close z-50">
                    <FontAwesomeIcon icon={faTimes} />
                </div>

                <div
                    className="z-50 absolute close bg-transparent rounded-full cursor-pointer bg-boon-beige flex justify-center items-center w-6 h-6 p-1"
                    style={{ top: '-10px', right: '-10px' }}
                >
                    <FontAwesomeIcon size="lg" icon={faTimesCircle} />
                </div>
            </React.Fragment>
        ),
    },
};

const Modal: React.FC<ModalProps> = props => {
    const id = useSelector((state: { modal: ModalState }) => state.modal.id);
    const show = useSelector((state: { modal: ModalState }) => state.modal.show);
    const onClose = useSelector((state: { modal: ModalState }) => state.modal.onClose);

    const [showModal, setShowModal] = useState(false);

    const isModalActive = props.id === id && show;

    // variants switch

    let containerClass = props.containerClass;
    let contentWrapperClass = props.contentWrapperClass;
    let closeElement = props.closeElement;

    if (props.variant) {
        if (!containerClass && variants[props.variant].containerClass) {
            containerClass = variants[props.variant].containerClass;
        }

        if (!contentWrapperClass && variants[props.variant].contentWrapperClass) {
            contentWrapperClass = variants[props.variant].contentWrapperClass;
        }

        if (!closeElement && variants[props.variant].closeElement) {
            closeElement = variants[props.variant].closeElement;
        }
    }

    const onClick = (event: React.MouseEvent) => {
        event.preventDefault();
        if (props.stopPropagation) {
            event.stopPropagation();
        }
        setShowModal(false);

        if (onClose) {
            onClose();
        }

        if (props.onClose) {
            props.onClose();
        }

        hideModal(props.id);
    };

    if (isModalActive) {
        setTimeout(() => {
            setShowModal(true);
        }, 100);

        return (
            <Portal>
                <div
                    className={
                        'Modal z-30 fixed w-full h-full top-0 left-0 flex items-center justify-center ' +
                        (showModal ? '' : 'opacity-0 pointer-events-none')
                    }
                    id={props.id}
                    data-modal-id={props.id}
                >
                    <style jsx>{styles}</style>

                    <div
                        onClick={onClick}
                        className="ModalOverlay -z-10 absolute w-full h-full bg-gray-900 opacity-50"
                    ></div>

                    <div
                        className={'ModalContainer ' + (containerClass ? containerClass : '') + ' ' + props.width}
                        onClick={e => {
                            if (props.stopPropagation) {
                                e.stopPropagation();
                            }
                        }}
                    >
                        <div className={'ModalContent ' + (contentWrapperClass ? contentWrapperClass : '')}>
                            <div className="flex flex-row justify-between items-start">
                                {props.title ? (
                                    <p className="flex-1 text-2xl font-bold">{props.title}</p>
                                ) : (
                                    <div className="flex-1"></div>
                                )}

                                <a className={props.titleBarClass} href="#" onClick={onClick}>
                                    {closeElement}
                                </a>
                            </div>

                            <div className="text-xs xsm:text-md">{props.children}</div>
                        </div>
                    </div>
                </div>
            </Portal>
        );
    } else {
        return <React.Fragment />;
    }
};

Modal.defaultProps = {
    width: 'w-11/12 max-w-xs',
    title: '',
    titleBarClass: '',
    variant: 'default',
    stopPropagation: false,
};

export default Modal;
